/** Additional notes to the accounts (api/v1/AdditionalNotes). Mirrors NoteRules on the API. */
import type { RichTextConfig } from './richText';

export type SingletonNoteType =
  | 'directorEmoluments'
  | 'staffCosts'
  | 'averageEmployees'
  | 'offBalanceSheet'
  | 'indebtedness'
  | 'financialCommitments'
  | 'auditInformation'
  | 'limitedByGuarantee'
  | 'relatedPartyDisplay';

/**
 * ap5-a5: check-box notes whose wording the preparer edits. They used to be flag-only, so ticking them
 * printed a heading with nothing under it. Defaults mirror NoteRules.Narrative on the API.
 */
export type NarrativeNoteType =
  'offBalanceSheet' | 'indebtedness' | 'financialCommitments' | 'auditInformation' | 'limitedByGuarantee';

export const NARRATIVE_NOTE_DEFAULTS: Record<NarrativeNoteType, string> = {
  offBalanceSheet:
    'The company is not party to any arrangements that are not reflected in its statement of financial ' +
    'position and for which the risks or benefits arising are material.',
  indebtedness:
    'No debts included under creditors fall due for payment more than five years after the reporting date, ' +
    'and no security has been given by the company in respect of any amount included under creditors.',
  financialCommitments:
    'There were no financial commitments, guarantees or contingencies at the reporting date that are not ' +
    'included in the statement of financial position.',
  /** BUG-075: audited accounts only. */
  auditInformation:
    "The auditor's report on the financial statements for the period was unqualified. The auditor's report did not " +
    'include a reference to any matters to which the auditor drew attention by way of emphasis without qualifying the report.',
  /** Issue #41: a company limited by guarantee. */
  limitedByGuarantee:
    'The company is limited by guarantee and does not have share capital. Each member undertakes to contribute an ' +
    'amount not exceeding £1 towards the assets of the company if it is wound up while they are a member, or ' +
    'within one year after they cease to be a member.',
};

/** ap5-a5: the rich-text editor the narrative check-box notes use for their wording. */
export function narrativeEditorConfig(): RichTextConfig {
  return {
    editable: true,
    spellcheck: true,
    height: '160',
    minHeight: '0',
    maxHeight: 'auto',
    width: 'auto',
    minWidth: '0',
    translate: 'yes',
    enableToolbar: true,
    showToolbar: true,
    placeholder: 'Enter the wording printed in the accounts...',
    defaultParagraphSeparator: '',
    sanitize: true,
    toolbarPosition: 'bottom',
    toolbarHiddenButtons: [
      ['fontName', 'fontSize', 'backgroundColor', 'strikeThrough', 'customClasses', 'insertHorizontalRule'],
    ],
  };
}

export type TextNoteType =
  'giftAid' | 'impairment' | 'intangibleAsset' | 'event' | 'exceptionalItem' | 'directorLoan' | 'additionalNote';

export type ListNoteType = 'relatedParty' | 'guarantee' | 'controllingParty' | 'advanceCredit' | TextNoteType;

export type NoteType = SingletonNoteType | ListNoteType;

export const LIST_NOTE_TYPES: ListNoteType[] = [
  'relatedParty',
  'guarantee',
  'controllingParty',
  'advanceCredit',
  'giftAid',
  'impairment',
  'intangibleAsset',
  'event',
  'exceptionalItem',
  'directorLoan',
  'additionalNote',
];

export const TEXT_NOTE_LABELS: Record<TextNoteType, string> = {
  giftAid: 'Gift Aid',
  impairment: 'Impairment of Assets',
  intangibleAsset: 'Intangible Asset',
  event: 'Event after Balance Sheet Date',
  exceptionalItem: 'Exceptional Item',
  directorLoan: 'Director’s Loan',
  additionalNote: 'Note',
};

/** Fixed current-year / previous-year rows per note type (the API renames rows by position). */
export const NOTE_ROW_NAMES: Partial<Record<NoteType, string[]>> = {
  directorEmoluments: ['Defined contribution plans', 'Defined benefit plans'],
  staffCosts: [
    'Administrator',
    'Production/Manufacturing',
    'Research & Development',
    'Sales',
    'Marketing',
    'Distribution',
    'Total',
  ],
  averageEmployees: ['Average number of employees'],
  relatedParty: ['Transaction value - income/(expenses)', 'Balance owed by/(owed to)'],
  guarantee: ['Maximum Liability Incurred', 'Amount Paid'],
  advanceCredit: ['Brought forward', 'Amount', 'Interest', 'Repaid', 'Written off', 'Waived', 'Carried forward'],
};

export interface NoteOption {
  value: string;
  label: string;
}

export interface NoteAmountRow {
  name: string;
  current: number | null;
  previous: number | null;
}

export interface NoteRow<T = any> {
  id: string;
  noteType: NoteType;
  isEnabled: boolean;
  sortOrder: number;
  name: string;
  detail: string;
  content: T;
}

export interface AdditionalNotesScreen {
  clientId: string;
  accountingPeriodId: string;
  periodLabel: string;
  isLocked: boolean;
  priorPeriodId: string | null;
  notes: NoteRow[];
  priorNotes: NoteRow[];
  countries: NoteOption[];
  relatedPartyTypes: NoteOption[];
  /**
   * task-108: the statutory notes an extension note can extend, as value/label pairs (BS_DEBTORS /
   * "Debtors" and so on). Optional so a screen served by an API without it still opens.
   */
  parentNoteTypes?: NoteOption[];
  /** Issue #41: the client is a company limited by guarantee, so the page offers the Company Limited by Guarantee note. */
  limitedByGuarantee?: boolean;
}

export interface SaveNotePayload {
  clientId: string;
  accountingPeriodId: string;
  id: string | null;
  noteType: NoteType;
  isEnabled: boolean;
  content: unknown;
}

export interface SetNoteEnabledPayload {
  clientId: string;
  accountingPeriodId: string;
  noteType: SingletonNoteType;
  isEnabled: boolean;
}

/** MAT_DIALOG_DATA for every additional-notes dialog. */
export interface NoteDialogData {
  clientId: string;
  accountingPeriodId: string;
  noteType: NoteType;
  note: NoteRow | null;
  priorNotes: NoteRow[];
  /** Other notes already saved of this same note type (current period), for an Extension Note to pick from. */
  existingNotes: NoteRow[];
  countries: NoteOption[];
  relatedPartyTypes: NoteOption[];
  /** task-108: the statutory notes an extension note can be printed under. */
  parentNoteTypes?: NoteOption[];
  title?: string;
  /** An LLP's Capium Tags panel reads "member(s)" wherever it would otherwise say "director(s)". */
  isLlp?: boolean;
  /** APT312: the client's type (ClientTypes); the Capium Tags panel offers the tags that type uses (see capiumTagGroupsFor). */
  clientType?: number | null;
}

/** A grid row bound to the dialog inputs (text while editing; the API parses the numbers). */
export interface EditableAmountRow {
  name: string;
  current: string;
  previous: string;
}

export function amountText(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

export function buildAmountRows(noteType: NoteType, saved?: NoteAmountRow[] | null): EditableAmountRow[] {
  return (NOTE_ROW_NAMES[noteType] ?? []).map((name, i) => ({
    name,
    current: amountText(saved?.[i]?.current),
    previous: amountText(saved?.[i]?.previous),
  }));
}

/** Blank previous-year cells default to the prior period's current-year values. */
export function fillPreviousFromPrior(rows: EditableAmountRow[], prior?: NoteRow | null): void {
  const priorRows: NoteAmountRow[] | undefined = prior?.content?.rows;
  if (!Array.isArray(priorRows)) {
    return;
  }
  rows.forEach((row, i) => {
    const value = priorRows[i]?.current;
    if (row.previous.trim() === '' && value !== null && value !== undefined) {
      row.previous = String(value);
    }
  });
}

export function priorSingleton(data: NoteDialogData): NoteRow | undefined {
  return data.priorNotes.find((n) => n.noteType === data.noteType);
}

export function priorByName(data: NoteDialogData, name: string): NoteRow | undefined {
  const key = (name ?? '').trim().toLowerCase();
  return key
    ? data.priorNotes.find((n) => n.noteType === data.noteType && (n.name ?? '').trim().toLowerCase() === key)
    : undefined;
}

export function amountPayload(
  rows: EditableAmountRow[],
): { name: string; current: string | null; previous: string | null }[] {
  const clean = (v: string) => (v === null || v === undefined || String(v).trim() === '' ? null : String(v).trim());
  return rows.map((r) => ({ name: r.name, current: clean(r.current), previous: clean(r.previous) }));
}

function parseAmount(value: string): number | null {
  const text = (value ?? '').toString().replace(/[,£\s]/g, '');
  if (text === '') {
    return null;
  }
  const negative = /^\(.*\)$/.test(text);
  const n = Number(negative ? text.slice(1, -1) : text);
  return isNaN(n) ? null : negative ? -n : n;
}

/** Sets the last row to the sum of the others (staff costs total), for both columns. */
export function applyRowTotal(rows: EditableAmountRow[]): void {
  if (rows.length < 2) {
    return;
  }
  const parts = rows.slice(0, -1);
  (['current', 'previous'] as const).forEach((column) => {
    const values = parts.map((r) => parseAmount(r[column]));
    if (values.every((v) => v === null)) {
      return;
    }
    const total = values.reduce<number>((sum, v) => sum + (v ?? 0), 0);
    rows[rows.length - 1][column] = String(Math.round(total * 100) / 100);
  });
}
